"use client";

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTheme } from "@/contexts/ThemeContext";
import {
  ButtonOutline,
  ButtonPrimary,
} from "@/components/common/StandardButton";
import { FileManagerConfig, FileSystemItem } from "@/types/fileManager";
import { generateImageSrc } from "@/utils/helper";
import { Check, X } from "lucide-react";
import { FileManagerContent } from "./FileManagerContent";
import { useFileManager } from "@/hooks/useFileManager";

interface FileManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: FileManagerConfig;
}

export const FileManagerModal: React.FC<FileManagerModalProps> = ({
  isOpen,
  onClose,
  config,
}) => {
  const { isDark } = useTheme();
  const [selectedItems, setSelectedItems] = useState<FileSystemItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Handle selection confirmation
  const handleConfirmSelection = (files: FileSystemItem[]) => {
    if (files.length === 0) return;

    // Filter out folders if only files should be selected
    const filesOnly = files.filter((item) => item && !item.isFolder);

    if (config.maxSelections && filesOnly.length > config.maxSelections) {
      setError(`You can only select up to ${config.maxSelections} files`);
      return;
    }

    // Ensure each file has a proper URL and preserve all asset details
    // Returns complete FileSystemItem with: assetId, name, path, url, size, mimeType,
    // contentType, createdAt, updatedAt (if available from API)
    const filesWithUrls = filesOnly.map((file) => ({
      ...file, // Preserve all original fields including contentType, createdAt, updatedAt
      url: file.url || generateImageSrc(file.path),
    }));

    console.log("📤 FileManager returning complete asset data:", filesWithUrls);
    config.onSelect(filesWithUrls);
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    setSelectedItems([]);
    if (config.onCancel) {
      config.onCancel();
    }
    onClose();
  };

  // Handle selection change from FileManagerContent
  const handleSelectionChange = (items: FileSystemItem[]) => {
    setSelectedItems(items);
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedItems([]);
      setError(null);
    }
  }, [isOpen]);

  const canConfirm =
    (selectedItems || []).length > 0 &&
    (selectedItems || []).every((item) => item && !item.isFolder);
  const selectedFileCount = (selectedItems || []).filter(
    (item) => item && !item.isFolder,
  ).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] h-[95vh] max-w-none max-h-none flex flex-col p-0">
        <DialogHeader
          className={`px-6 py-4 border-b ${isDark ? "bg-auth-card border-auth-background" : "bg-white border-gray-200"}`}
        >
          <DialogTitle>File Manager</DialogTitle>
          <DialogDescription>
            {config.selectionMode === "single"
              ? "Select a file or upload new files"
              : `Select up to ${config.maxSelections || "unlimited"} files or upload new ones`}
          </DialogDescription>
        </DialogHeader>

        <FileManagerContent
          config={config}
          onConfirmSelection={handleConfirmSelection}
          onCancel={handleCancel}
          onSelectionChange={handleSelectionChange}
          mode="modal"
          className="flex-1"
        />

        <DialogFooter
          className={`px-6 py-4 border-t ${isDark ? "bg-auth-card border-auth-background" : "bg-white border-gray-200"}`}
        >
          <ButtonOutline
            onClick={handleCancel}
            className="h-10 px-3 flex items-center space-x-2 text-sm transition-colors"
          >
            <X className="w-4 h-4" />
            <span>Cancel</span>
          </ButtonOutline>
          <ButtonPrimary
            onClick={() => handleConfirmSelection(selectedItems || [])}
            disabled={!canConfirm}
            className="hover:shadow-lg hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:scale-100 disabled:shadow-sm h-10 px-3 flex items-center space-x-2 text-sm"
          >
            <Check className="w-4 h-4" />
            <span>
              Select {selectedFileCount > 0 && `(${selectedFileCount})`}
            </span>
          </ButtonPrimary>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
